import type { MiddlewareAPI, Middleware } from '@reduxjs/toolkit';
import { updateAlertMessage, selectPageTranslationData } from './common.slice';
import { classifyError } from '../util/telemetry/error/error-classifier.util';
// START-CodeLeap [04-Sep-2026]: [#18188866946 PR-2615 review fixes] translated middleware copy.
// Note: this makes api-integration the first store-side consumer of util/util, which transitively
// pulls bootstrap's Modal. Checked before adding it — util/util has no import path back to
// api-integration/index, so there is no cycle, and the Modal import only binds a class (no DOM
// access at module scope) in an app that loads Bootstrap regardless. The alternative, a separate
// module for one translation guard, would fork the translation helpers away from
// getTranslatedText, which rule #41 argues against more strongly.
// The RootState import is `import type`, so it is erased and cannot cycle with index.ts.
import { getTranslatedTextOrFallback } from '../util/util';
import type { RootState } from './index';
// /END-CodeLeap [04-Sep-2026]: [#18188866946 PR-2615 review fixes]

// Define the expected shape of an RTK Query rejected action without using 'any'
interface RtkRejectedAction {
  type: string;
  payload?: {
    status?: string | number;
    originalStatus?: number;
    error?: string;
    data?: unknown;
  };
  error?: {
    name?: string;
    message?: string;
  };
  meta?: {
    aborted?: boolean;
    condition?: boolean;
    rejectedWithValue?: boolean;
  };
}

// Type guard to safely check if an unknown action is an RTK Query rejected action
function isRtkRejectedAction(action: unknown): action is RtkRejectedAction {
  return (
    typeof action === 'object' &&
    action !== null &&
    'type' in action &&
    typeof (action as Record<string, unknown>).type === 'string' &&
    (action as Record<string, string>).type.endsWith('rejected')
  );
}

// START-CodeLeap [04-Sep-2026]: [#18188866946 PR-2615 review fixes]
/**
 * Resolve a translation key from inside middleware.
 *
 * Middleware has no hooks, but it does not need any: PageTranslation lives in redux
 * (common.slice `selectPageTranslationData`) and `getTranslatedText` is a pure (key, array)
 * function, so `api.getState()` is the whole dependency — `usePageTranslation` is itself only
 * `useAppSelector(selectPageTranslationData)`. This is why the messages in this file did not
 * have to stay hardcoded English.
 *
 * The unresolvable-key guard lives in util's getTranslatedTextOrFallback, shared rather than
 * re-derived here; see its comment for why a fallback is required at all.
 *
 * Params : api (MiddlewareAPI), key (string), fallback (string) - resolved English default
 * Returns: string - the translated text, or `fallback` when it cannot be resolved
 */
const translateFromState = (api: MiddlewareAPI, key: string, fallback: string): string =>
   getTranslatedTextOrFallback(key, selectPageTranslationData(api.getState() as RootState), fallback);
// /END-CodeLeap [04-Sep-2026]: [#18188866946 PR-2615 review fixes]

/**
 * Log a warning and show a toast!
 * This middleware catches all rejected RTK Query actions globally.
 * It ensures we handle client-side network failures identically
 * regardless of whether the request originated from `secure.ts` or `api.admin.ts`.
 */
export const rtkQueryErrorLogger: Middleware = (api: MiddlewareAPI) => (next) => (action: unknown) => {
    // Use the strict type guard instead of casting to 'any'
    if (isRtkRejectedAction(action)) {
      // Ignore intentional cancellations (e.g., user navigated away, component unmounted)
      if (action.meta?.aborted || action.meta?.condition) {
        return next(action);
      }

      const errData = action.payload;
      const errorObj = action.error;

      // 4. Observability: Log a structured error for maintainers
      console.error('RTK Query Error:', { action, status: errData?.status || errorObj?.name });

      // 7. DRY: Rely on our central classification utility
      const classification = classifyError(errData || errorObj);

      // START-CodeLeap [02-Sep-2026]: [#18188866946] the baseQuery probe already identified which
      // attachments died, so name them instead of falling through to a generic upload message.
      if (errData?.error === 'UNREADABLE_ATTACHMENT') {
         const names = (errData?.data as { unreadableFiles?: string[] } | undefined)?.unreadableFiles ?? [];
         // START-CodeLeap [04-Sep-2026]: [#18188866946 PR-2615 review fixes] translated, was two
         // hardcoded English sentences (rule #36). `{files}` matches the placeholder convention the
         // composer's cap hint already uses, and is substituted after resolution so it works on the
         // translated string and on the English fallback alike.
         const message = names.length
            ? translateFromState(
                 api,
                 'executionFileUpload.unreadable.message',
                 'These files can no longer be read: {files}. They may have been renamed, moved or deleted since you attached them. Remove them and attach them again.',
              ).replace('{files}', names.join(', '))
            : translateFromState(
                 api,
                 'executionFileUpload.unreadableSingular.message',
                 'An attached file can no longer be read. It may have been renamed, moved or deleted since you attached it. Remove it and attach it again.',
              );
         // /END-CodeLeap [04-Sep-2026]: [#18188866946 PR-2615 review fixes]
         api.dispatch(updateAlertMessage({
            status: 'error',
            message,
            isNetworkError: true,
            timeout: null,
            isManualDismiss: true
         }));
         return next(action);
      }
      // /END-CodeLeap [02-Sep-2026]: [#18188866946]

      if (classification === 'TIMEOUT_ERROR') {
         api.dispatch(updateAlertMessage({ 
            status: 'error', 
            message: 'The execution took too long to complete. Please try again.',
            isNetworkError: true,
            timeout: null,
            isManualDismiss: true
         }));
      }
      
      else if (classification === 'NETWORK_ERROR') {
         let uiMessage = 'A network error occurred.';

         if (!navigator.onLine) {
            uiMessage = 'You are currently offline. Please reconnect to the internet to continue.';
         } else {
            uiMessage = 'Your connection was interrupted or your network changed. Please try again.';
         }

         api.dispatch(updateAlertMessage({ 
            status: 'error', 
            message: uiMessage,
            isNetworkError: true,
            timeout: null,
            isManualDismiss: true
         }));
      }

      else if (classification === 'SERVER_ERROR' || classification === 'PARSING_ERROR') {
        let message = 'The server encountered a problem. Please try again later.';
        
        if (classification === 'PARSING_ERROR' && errData?.originalStatus !== 502 && errData?.originalStatus !== 503 && errData?.originalStatus !== 504) {
            message = 'We received an unexpected response from the server. Please try again.';
        }
        
        api.dispatch(updateAlertMessage({ 
            status: 'error', 
            message: message,
            isNetworkError: true, // Engage Priority Lock for Server Crashes too
            timeout: null,
            isManualDismiss: true
        }));
      }
    }

    return next(action);
  };
